import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  OnInit,
  output,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { translateObject, TranslocoService } from '@jsverse/transloco';
import { DropdownObject, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { ItemLabelsComponent } from 'app/components/item-labels/item-labels.component';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { DatasourceManagerStore } from 'app/modules/dashboard/stores/datasource-manager.store';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { Dashboard, Widget } from 'app/providers/bridge-api/dashboard.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from 'app/providers/semantria-api/semantria-users.service';
import {
  AiInsightFeed,
  AiInsightFeedParams,
  AiInsightsGroupFeedService,
} from 'app/providers/storage-api/ai-insights-group-feeds.service';
import {
  ItemLabel,
  ItemLabelWrapper,
  ObjectLabel,
  ObjectLabelService,
} from 'app/providers/storage-api/object-label.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { noWhitespaceValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { DatasourceManager } from '../data-source-chooser/data-source-chooser.component';
import { WidgetInsightFeedSource } from '../widget-insight-feed/widget-insight-feed.component';
import { WidgetAiInsightsService } from '../../services/widget-ai-insights.service';

type FilterType = 'publisher' | 'project' | 'dashboard' | 'dataSource';

export interface CreateFeedModalInput {
  existingFeeds: AiInsightFeed[];
  widget: Widget;
  project: Project;
  dashboard: Dashboard;
  action: 'create' | 'edit';
}
@Component({
  selector: 'app-create-insight-feed-dialog',
  imports: [AppSharedModule, ItemLabelsComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './create-insight-feed-dialog.component.html',
  styleUrl: './create-insight-feed-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateInsightFeedDialogComponent implements OnInit {
  options = input<CreateFeedModalInput>();

  modalClosed = output<boolean>();

  createFeedForm: FormGroup;
  dropdownTags: DropdownObject[];
  filterTypes: Signal<
    {
      type: FilterType;
      sectionLabel: string;
      isOpen: boolean;
      data: DropdownObject[];
      inputLabel: string;
    }[]
  > = computed(() => null);

  feedVisibility: DropdownObject[];
  nameStatus: ErrorObject;
  private users: SemantriaUser[] = [];
  private projects: Project[] = [];

  labelItem: ItemLabelWrapper = { type: 'insightFeed', labels: [], id: null };
  areLabelsVisible = signal(false);
  private selectedFeed: AiInsightFeed;
  isLoading = signal(true);
  isLoadingFilters = signal(true);
  hasFeedNameChanged = signal(true);

  private fb = inject(FormBuilder);
  private translocoService = inject(TranslocoService);
  private widgetAiInsightsService = inject(WidgetAiInsightsService);
  private aiInsightsGroupFeedService = inject(AiInsightsGroupFeedService);
  private userService = inject(SemantriaUsersService);
  private labelService = inject(ObjectLabelService);
  private groupService = inject(GroupService);
  private dialogService = inject(DialogService);
  private authService = inject(AuthService);
  private alertService = inject(AlertService);
  private projectService = inject(ProjectService);
  private readonly datasourceManagerStore = inject(DatasourceManagerStore);

  private existingFeedNameFn = (control: AbstractControl<string>): ValidationErrors | null => {
    const existingName = this.options().existingFeeds.find(
      (f: AiInsightFeed) =>
        f.name.trim() === control.value?.trim() && f.name.trim() !== this.selectedFeed?.name.trim()
    );
    if (control.value && existingName) {
      return { duplicate: true };
    }
  };
  constructor() {
    this.widgetAiInsightsService.aiInsightTags$.pipe(takeUntilDestroyed()).subscribe((res) => {
      if (res) {
        this.dropdownTags = res
          .map((tag) => ({ title: tag, id: tag }))
          .sort((a, b) => a.title.localeCompare(b.title));
        this.isLoading.set(false);
      }
    });
  }

  ngOnInit() {
    this.widgetAiInsightsService.loadInsightTags({
      projectId: this.options().dashboard.project_id,
    });

    this.feedVisibility = [
      {
        id: 'allUsers',
        title: this.translocoService.translate(
          'widgets.create-insight-feed-dialog.all-users-in-project'
        ),
      },
      {
        id: 'labels',
        title: this.translocoService.translate(
          'widgets.create-insight-feed-dialog.access-by-label'
        ),
      },
    ];

    this.createFeedForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          noWhitespaceValidator,
          Validators.maxLength(200),
          this.existingFeedNameFn,
        ],
      ],
      tags: [],
      filters: this.fb.group({
        publisher: [],
        project: [],
        dashboard: [],
        dataSource: [],
      }),
      visibility: [this.feedVisibility[0].id, Validators.required],
    });

    if (this.options().action === 'edit') {
      this.selectedFeed = this.options().existingFeeds.find(
        (f) => f.id === this.options().widget.payload.selectedInsightFeedValue
      );

      this.labelItem.labels = (this.selectedFeed.objectLabels as ObjectLabel[]) ?? [];
    }

    this.setUpFormData();

    this.createFeedForm.statusChanges.subscribe((status) =>
      this.createErrorObjectAndValidateInput(status)
    );

    // Subscribe to name field changes to update hasFeedNameChanged
    if (this.options().action === 'edit') {
      this.createFeedForm.get('name')?.valueChanges.subscribe(() => {
        const currentName = this.createFeedForm.get('name')?.value?.trim();
        const originalName = this.selectedFeed?.name?.trim();
        this.hasFeedNameChanged.set(currentName && currentName !== originalName);
      });

      // Initialize the signal
      const currentName = this.createFeedForm.get('name')?.value?.trim();
      const originalName = this.selectedFeed?.name?.trim();
      this.hasFeedNameChanged.set(currentName && currentName !== originalName);
    }
  }
  private async setUpFormData() {
    this.users = await this.userService.getUsers();
    const projectList = await this.projectService.getProjects(false);
    this.projects = projectList.items;

    const filterTypeNames: {
      type: FilterType;
      inputLabel: string;
    }[] = [
      { type: 'publisher', inputLabel: 'publishers' },
      { type: 'project', inputLabel: 'projects' },
      { type: 'dashboard', inputLabel: 'dashboards' },
      { type: 'dataSource', inputLabel: 'data-sources' },
    ];

    this.filterTypes = computed(() =>
      filterTypeNames.map(({ type, inputLabel }) => ({
        type,
        isOpen: this.options()?.action !== 'create',
        data: this.getFilterData(type),
        sectionLabel: this.translocoService.translate(
          `widgets.create-insight-feed-dialog.add-${inputLabel}-filter`
        ),
        inputLabel: this.translocoService.translate(
          `widgets.create-insight-feed-dialog.${inputLabel}`
        ),
      }))
    );
    if (this.options().action === 'edit') {
      const filters = this.mapQueryFiltersToFormData(this.selectedFeed.insightQuery.properties);

      const formData = {
        name: this.selectedFeed.name,
        tags: this.selectedFeed.insightQuery.tags,
        filters: {
          publisher: filters.publishers,
          project: filters.projects,
          dashboard: filters.dashboards,
          dataSource: filters.dataSources,
        },
        visibility: this.labelItem.labels.length ? 'labels' : 'allUsers',
      };
      this.createFeedForm.setValue(formData);
      this.areLabelsVisible.set(!!this.labelItem.labels.length);
    }
    this.isLoadingFilters.set(false);
  }

  private getFilterData(type: FilterType) {
    switch (type) {
      case 'publisher':
        const users =
          this.authService.isAccountAdmin || this.authService.hasAllAccess
            ? this.users
            : this.users.filter((u) => u.user_id === this.authService.currentUserId);

        return users
          .map((u) => ({
            id: u.user_id,
            title: `${u.first_name} ${u.last_name}`,
            subtitle: u.email,
          }))
          .sort((a, b) => a.title.localeCompare(b.title));

      case 'project':
        return this.projects.map((p) => ({ id: p.id, title: p.name }));
      case 'dashboard':
        return this.options()
          .project.dashboards.map((d) => ({ id: d.id, title: d.title }))
          .sort((a, b) => a.title.localeCompare(b.title));

      case 'dataSource':
        const dataSources = this.datasourceManagerStore.getDatasources();
        const dsManager = new DatasourceManager(dataSources, '');
        return dsManager
          .search(null)
          .map((ds) => ({ id: ds.value.id, title: ds.display }))
          .sort((a, b) => a.title.localeCompare(b.title));

      default:
        return [];
    }
  }

  private createErrorObjectAndValidateInput(status: FormControlStatus): void {
    this.nameStatus = ErrorObjectUtils.create();
    const nameError = this.createFeedForm.controls.name.errors;
    this.validateInput(status, nameError, this.nameStatus);
  }

  private validateInput(formStatus: string, errors: ValidationErrors, errorObj: ErrorObject) {
    if (formStatus === 'INVALID' && errors) {
      switch (Object.keys(errors)[0]) {
        case 'required':
        case 'whitespace':
          ErrorObjectUtils.setError(
            errorObj,
            this.translocoService.translate('widgets.create-insight-feed-dialog.feed-name-required')
          );
          break;
        case 'maxlength':
          ErrorObjectUtils.setError(
            errorObj,
            this.translocoService.translate(
              'widgets.create-insight-feed-dialog.char-limit-reached',
              {
                maxlength: errors.maxlength.requiredLength,
              }
            )
          );
        case 'duplicate':
          ErrorObjectUtils.setError(
            errorObj,
            this.translocoService.translate('widgets.create-insight-feed-dialog.feed-name-exists')
          );
      }
    } else {
      ErrorObjectUtils.setNoError(errorObj);
    }
  }
  toggleFilterDisplay(f: any) {
    f.isOpen = !f.isOpen;
  }

  clearFilter(f) {
    this.createFeedForm.get('filters').get(f.type).setValue([]);
    this.toggleFilterDisplay(f);
  }

  onFeedVisibilityChange($event) {
    const selected: 'allUsers' | 'labels' = $event.detail.selected.id;
    this.areLabelsVisible.set(selected === 'labels');
    if (selected === 'allUsers') {
      this.labelItem.labels = [];
    }
  }

  onCancel() {
    this.modalClosed.emit(false);
  }

  async onCreateOrUpdate(action: 'create' | 'edit') {
    const setLabels = (feedId): Promise<unknown> => {
      let labels = [];
      if (this.createFeedForm.get('visibility').value === 'labels') {
        labels = this.labelItem.labels.map((l) => l.id);
      }
      return this.labelService.setLabels(
        this.groupService.getSelectedGroupId(),
        labels,
        'feed',
        feedId
      );
    };
    const filters = {
      publishers: this.createFeedForm.get('filters.publisher').value || [],
      projects: this.createFeedForm.get('filters.project').value || [],
      dashboards: this.createFeedForm.get('filters.dashboard').value || [],
      dataSources: this.createFeedForm.get('filters.dataSource').value || [],
    };

    const feedSource = this.options().widget.payload.feedSource as WidgetInsightFeedSource;

    const requestParams: AiInsightFeedParams = {
      projectId: action === 'create' ? this.options().dashboard.project_id : undefined,
      dashboardId:
        action === 'create' && feedSource !== 'allProjectFeeds'
          ? this.options().dashboard.id
          : undefined,
      name: this.createFeedForm.get('name').value.trim(),
      insightQuery: {
        properties: this.mapFormDataToQueryFilters(filters),
        tags: this.createFeedForm.get('tags').value,
      },
    };

    if (action === 'create') {
      try {
        this.isLoading.set(true);
        const res = await this.aiInsightsGroupFeedService.createAiInsightsGroupFeed(requestParams);
        await setLabels(res.id);
        this.modalClosed.emit(true);
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('widgets.create-insight-feed-dialog.could-not-save-feed'),
          error
        );
        this.isLoading.set(false);
      }
    } else {
      try {
        this.isLoading.set(true);
        await setLabels(this.options().widget.payload.selectedInsightFeedValue);
        await this.aiInsightsGroupFeedService.updateAiInsightsGroupFeed(
          this.options().widget.payload.selectedInsightFeedValue,
          requestParams
        );
        this.modalClosed.emit(true);
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('widgets.create-insight-feed-dialog.could-not-save-feed'),
          error
        );
        this.isLoading.set(false);
      }
    }
  }

  onDelete() {
    const translations = translateObject('widgets.create-insight-feed-dialog.delete-dialog');
    this.dialogService
      .showSimpleDialog({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          try {
            this.isLoading.set(true);
            await this.aiInsightsGroupFeedService.deleteAiInsightsGroupFeed(
              this.options().widget.payload.selectedInsightFeedValue
            );
            this.modalClosed.emit(true);
          } catch (error) {
            this.alertService.showErrorToast(
              this.translocoService.translate(
                'widgets.create-insight-feed-dialog.could-not-delete-feed'
              ),
              error
            );
            this.isLoading.set(false);
          }
        }
      });
  }

  onLabelApplied(label: Partial<ItemLabel>) {
    if (!label.id) {
      return;
    }
    this.labelItem.labels = [...this.labelItem.labels, label];
  }

  onLabelRemoved(labelId: string) {
    this.labelItem.labels = this.labelItem.labels.filter((l) => l.id !== labelId);
  }

  private mapQueryFiltersToFormData(queryProperties: string[]) {
    const filters = {
      publishers: [] as string[],
      projects: [] as string[],
      dashboards: [] as string[],
      dataSources: [] as string[],
    };

    for (const prop of queryProperties) {
      const [key, value] = prop.split(':');

      switch (key) {
        case 'userId':
          filters.publishers.push(value);
          break;
        case 'projectId':
          filters.projects.push(value);
          break;
        case 'dashboardId':
          filters.dashboards.push(value);
          break;
        case 'analysisId':
          filters.dataSources.push(value);
          break;
      }
    }
    return filters;
  }

  private mapFormDataToQueryFilters(filters: {
    publishers: string[];
    projects: string[];
    dashboards: string[];
    dataSources: string[];
  }) {
    const props = [];
    props.push(
      ...filters.publishers.map((publisher) => `userId:${publisher}`),
      ...filters.projects.map((project) => `projectId:${project}`),
      ...filters.dashboards.map((dashboard) => `dashboardId:${dashboard}`),
      ...filters.dataSources.map((dataSource) => `analysisId:${dataSource}`)
    );
    return props;
  }
}
